import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../utils/axios.ts';

// Types
export interface Role {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  is_active: boolean;
  permissions?: Permission[];
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  module: string;
  action: string;
  feature_name?: string;
  is_active: boolean;
}

export interface RoleFormData {
  name?: string;
  display_name: string;
  description?: string;
  permission_ids: number[];
}

export interface AssignRoleData {
  user_id: number;
  role_id: number;
  group_id?: number;
}

// API Functions
const rolesApi = {
  // Get all roles
  getRoles: (params: { search?: string; per_page?: number; page?: number } = {}) =>
    api.get('/roles', { params }),

  // Get single role
  getRole: (id: number) =>
    api.get(`/roles/${id}`),

  // Create role
  createRole: (data: RoleFormData) =>
    api.post('/roles', data),

  // Update role
  updateRole: (id: number, data: RoleFormData) =>
    api.put(`/roles/${id}`, data),

  // Delete role
  deleteRole: (id: number) =>
    api.delete(`/roles/${id}`),

  // Get available permissions for role assignment
  getAvailablePermissions: () =>
    api.get('/roles/permissions/available'),

  // Get available roles for admin assignment
  getAvailableRoles: () =>
    api.get('/roles/available'),

  // Assign role to user
  assignRole: (data: AssignRoleData) =>
    api.post('/roles/assign', data),

  // Remove role from user
  removeRole: (data: AssignRoleData) =>
    api.delete('/roles/remove', { data }),

  // Get user permissions
  getUserPermissions: () =>
    api.get('/user/permissions'),
};

// Hooks
export const useRoles = (params: { search?: string; per_page?: number; page?: number } = {}) => {
  return useQuery({
    queryKey: ['roles', params],
    queryFn: () => rolesApi.getRoles(params),
    select: (data) => data.data,
  });
};

export const useRole = (id: number) => {
  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => rolesApi.getRole(id),
    select: (data) => data.data.data,
    enabled: !!id,
  });
};

export const useAvailablePermissions = () => {
  return useQuery({
    queryKey: ['permissions', 'available'],
    queryFn: () => rolesApi.getAvailablePermissions(),
    select: (data) => data.data,
  });
};

export const useAvailableRoles = () => {
  return useQuery({
    queryKey: ['roles', 'available'],
    queryFn: () => rolesApi.getAvailableRoles(),
    select: (data) => data.data,
  });
};

export const useUserPermissions = () => {
  return useQuery({
    queryKey: ['user', 'permissions'],
    queryFn: () => rolesApi.getUserPermissions(),
    select: (data) => data.data,
  });
};

export const useCreateRole = () => {
  return useMutation({
    mutationFn: (data: RoleFormData) => rolesApi.createRole(data),
  });
};

export const useUpdateRole = () => {
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & RoleFormData) => rolesApi.updateRole(id, data),
  });
};

export const useDeleteRole = () => {
  return useMutation({
    mutationFn: (id: number) => rolesApi.deleteRole(id),
  });
};

export const useAssignRole = () => {
  return useMutation({
    mutationFn: (data: AssignRoleData) => rolesApi.assignRole(data),
  });
};

export const useRemoveRole = () => {
  return useMutation({
    mutationFn: (data: AssignRoleData) => rolesApi.removeRole(data),
  });
};

// Utility hook to check if user has specific permission
export const useHasPermission = (permission: string) => {
  const { data: userPermissions } = useUserPermissions();
  
  return {
    hasPermission: userPermissions?.permissions?.includes(permission) || userPermissions?.is_admin || userPermissions?.is_group_owner || false,
    isAdmin: userPermissions?.is_admin || false,
    isGroupOwner: userPermissions?.is_group_owner || false,
    permissions: userPermissions?.permissions || [],
    role: userPermissions?.role || null,
  };
};

// Utility hook to check if user has any of the given permissions
export const useHasAnyPermission = (permissions: string[]) => {
  const { data: userPermissions } = useUserPermissions();
  
  const hasAnyPermission = permissions.some(permission => 
    userPermissions?.permissions?.includes(permission)
  ) || userPermissions?.is_admin || userPermissions?.is_group_owner || false;
  
  return {
    hasAnyPermission,
    isAdmin: userPermissions?.is_admin || false,
    isGroupOwner: userPermissions?.is_group_owner || false,
    permissions: userPermissions?.permissions || [],
    role: userPermissions?.role || null,
  };
};

// Utility hook to check if user has all of the given permissions
export const useHasAllPermissions = (permissions: string[]) => {
  const { data: userPermissions } = useUserPermissions();
  
  const hasAllPermissions = permissions.every(permission => 
    userPermissions?.permissions?.includes(permission)
  ) || userPermissions?.is_admin || userPermissions?.is_group_owner || false;
  
  return {
    hasAllPermissions,
    isAdmin: userPermissions?.is_admin || false,
    isGroupOwner: userPermissions?.is_group_owner || false,
    permissions: userPermissions?.permissions || [],
    role: userPermissions?.role || null,
  };
}; 